import type { OneOnOneColleagueItem } from '@/lib/colleagues.ts';

/** What the Team page can be sorted on. */
export type TeamSortKey = 'next' | 'name' | 'support';

export interface TeamSort {
  key: TeamSortKey;
  direction: 'asc' | 'desc';
}

/** How the page opens: who is up first, then who was seen last, then by name. */
export const DEFAULT_TEAM_SORT: TeamSort = { key: 'next', direction: 'asc' };

/**
 * The colleagues in the order asked for. Descending is the ascending order
 * turned round, whatever the key — one rule to learn.
 */
export function sortColleagues(
  colleagues: readonly OneOnOneColleagueItem[],
  sort: TeamSort,
): OneOnOneColleagueItem[] {
  const sorted = [...colleagues].sort(COMPARE[sort.key]);
  return sort.direction === 'asc' ? sorted : sorted.reverse();
}

const COMPARE: Record<
  TeamSortKey,
  (a: OneOnOneColleagueItem, b: OneOnOneColleagueItem) => number
> = {
  name: (a, b) => byName(a, b),
  next: (a, b) => byNext(a, b) || byName(a, b),
  /* Helped first, and among those the ones Wundo already remembers. */
  support: (a, b) =>
    Number(b.supported) - Number(a.supported) ||
    Number(b.memory) - Number(a.memory) ||
    byNext(a, b) ||
    byName(a, b),
};

function byName(a: OneOnOneColleagueItem, b: OneOnOneColleagueItem): number {
  return a.label.localeCompare(b.label);
}

/* Soonest planned first; then, without anything planned, the most recently seen. */
function byNext(a: OneOnOneColleagueItem, b: OneOnOneColleagueItem): number {
  if (a.next_one_on_one_at && b.next_one_on_one_at) {
    return a.next_one_on_one_at.localeCompare(b.next_one_on_one_at);
  }
  if (a.next_one_on_one_at) return -1;
  if (b.next_one_on_one_at) return 1;
  return (b.last_one_on_one_at ?? '').localeCompare(a.last_one_on_one_at ?? '');
}
